/**
 * src/components/agent/AtMentionPicker.jsx
 *
 * Phase 82-04 — Chat System Unification, composer menu.
 *
 * Renders the @-mention autocomplete as a floating list above the composer.
 * Stateless — the parent drives results + cursor via useAtMentions.
 */

import { Briefcase, DollarSign, CheckSquare, User } from 'lucide-react';
import {
  menuPopoverStyle,
  menuListStyle,
  menuItemStyle,
  menuItemBodyStyle,
  menuItemLabelStyle,
  menuItemTypeTagStyle,
  menuEmptyStyle,
} from './ChatComposerV2.styles';

const TYPE_ICON = {
  project: Briefcase,
  deal: DollarSign,
  task: CheckSquare,
  person: User,
};

function EntityIcon({ type, active }) {
  const Icon = TYPE_ICON[type] || User;
  return <Icon size={14} strokeWidth={2} color={active ? 'var(--accent)' : 'var(--tm)'} />;
}

export default function AtMentionPicker({
  open,
  results,
  cursor,
  onHover,
  onSelect,
}) {
  if (!open) return null;
  return (
    <div
      style={menuPopoverStyle}
      role="listbox"
      aria-label="Mention picker"
      data-testid="at-mention-picker"
    >
      <div style={menuListStyle}>
        {results.length === 0 ? (
          <div style={menuEmptyStyle} data-testid="at-mention-empty">
            No matches
          </div>
        ) : (
          results.map((hit, idx) => (
            <button
              key={`${hit.type}:${hit.row.id}`}
              type="button"
              role="option"
              aria-selected={idx === cursor}
              onMouseEnter={() => onHover?.(idx)}
              onMouseDown={(e) => { e.preventDefault(); onSelect?.(hit); }}
              style={menuItemStyle(idx === cursor)}
              data-testid={`at-mention-item-${hit.type}-${hit.row.id}`}
              data-mention-type={hit.type}
            >
              <EntityIcon type={hit.type} active={idx === cursor} />
              <span style={menuItemBodyStyle}>
                <span style={menuItemLabelStyle}>{hit.label || '(unnamed)'}</span>
              </span>
              <span style={menuItemTypeTagStyle}>{hit.type}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
